import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { IoMdSearch } from 'react-icons/io';
import { format, addHours } from 'date-fns';

import { validateUserDataService } from '../../../../services/user';
import getUF from '../../../../utils/getUF';
import getValidationErrors from '../../../../utils/getValidationErrors';
import validationCPF from '../../../../utils/validationCPF';
import { useUser } from '../../../../hooks/user';
import useCep from '../../../../hooks/cep';
import { getProfiles } from '../../../../services/profile';
import { findInstitutionsByFilter } from '../../../../services/institution';
import { usePermission } from '../../../../hooks/permission';
import {
  useCurrentCallback,
  useCurrentEffect,
} from '../../../../lib/use-current-effect';

import InputWithoutTitle from '../../../../components/InputWithoutTitle';
import InputMaskAlternative from '../../../../components/InputMaskAlternative';
import RequiredFieldInformation from '../../../../components/RequiredFieldInformation';
import InputWithoutTitleAndFormattedToLower from '../../../../components/InputWithoutTitleAndFormattedToLower';
import InputCheckWithoutForm from '../../../../components/InputCheckWithoutForm';
import { Address } from '../../../../entities/Address';

import {
  ComponentsUserRegisterEditProps,
  ComponentsUserRegisterEditRef,
} from '..';

import {
  AddressGrid,
  AddressToggle,
  Container,
  Field,
  GridContent,
  InputSelect,
} from './styles';

export interface PersonalDataProps {
  username?: string;
  email?: string;
  name?: string;
  profileType?: string;
  cpf?: string;
  birthday?: string;
  phone?: string;
  gender?: 'M' | 'F' | 'ND';
  neighborhood?: string;
  street?: string;
  number?: number;
  complement?: string;
  city?: string;
  cep?: string;
  addressUf?: string;
  /**
   * Instituição do vínculo (Fase 0). Não é campo do usuário: o back grava em
   * `institution_users`, que é a autoridade do escopo por instituição. Só aparece na
   * **criação** — trocar o vínculo de quem já existe é rebind (tarefa 0.15).
   */
  institutionId?: string;
}

type InputOptions = {
  label: string;
  value: string;
};

/**
 * Único papel cross-tenant (decisão de 2026-08-11, reafirmada em 2026-08-12): ele vê
 * todos os tenants e **não** recebe vínculo com instituição — o back recusa com 400
 * se o campo vier. Todo outro papel de equipe exige instituição.
 */
const ADMINISTRATOR_ROLE = 'administrator';

/**
 * O endereço é opcional e fica atrás de um checkbox. Antes disto os campos eram
 * sempre exibidos e sempre enviados: quem não preenchia nada mandava
 * `{cep: "", city: "", ..., uf: ""}`, e o back — em que **toda** coluna de
 * `addresses` é nullable — gravava uma linha de strings vazias, indistinguível de
 * um endereço preenchido em branco. O `uf: ""` ainda virava NULL calado, porque o
 * `BrazilianStateEnum[""]` não resolve. Não dava erro; sujava a base.
 */
const hasAddressContent = (address?: Address): boolean =>
  !!address &&
  [
    address.street,
    address.number,
    address.complement,
    address.neighborhood,
    address.city,
    address.cep,
    address.uf,
  ].some((value) => `${value ?? ''}`.trim() !== '');

const PersonalData: React.ForwardRefRenderFunction<
  ComponentsUserRegisterEditRef,
  ComponentsUserRegisterEditProps
> = ({ onSaveSucceed }, ref) => {
  const { hasPermission } = usePermission();
  const { saveLocal, save, user, cleanLocal, setPendingInstitutionId } =
    useUser();
  const { getAddressData, addressData, error } = useCep();

  const formRef = useRef<FormHandles>(null);
  const [ufProps] = useState(getUF);

  const canChangeProfile = useMemo(
    () => !user?.id || hasPermission('edit_profile'),
    [hasPermission, user],
  );

  const genders = [
    { value: 'M', label: 'Masculino' },
    { value: 'F', label: 'Feminino' },
    { value: 'ND', label: 'Não declarar' },
  ];

  const [profiles, setProfiles] = useState<InputOptions[]>([
    {
      value: user?.role ?? '',
      label: '',
    },
  ]);

  useCurrentEffect((isCurrent) => {
    getProfiles().then((resp) => {
      const selectableProfiles =
        resp
          .filter(({ type }) => type !== 'patient')
          .map(({ type, title }) => ({
            value: type ?? '',
            label: title ?? '',
          })) ?? [];

      isCurrent() && setProfiles(selectableProfiles);
    });
  }, []);

  /**
   * Instituições para o vínculo (Fase 0).
   *
   * Só é buscado — e o campo só é exibido — na **criação**: o vínculo de quem já
   * existe não vem na resposta de usuário (ele mora em `institution_users`, e
   * `GET /users/{id}` não o devolve), então um select preenchido em branco na edição
   * sugeriria que salvar iria desvincular. Rebind é a tarefa 0.15.
   */
  const isCreating = useMemo(() => !user?.id, [user]);

  /**
   * Revela os campos de endereço. Nasce desmarcado na criação e é ligado sozinho
   * na edição de quem já tem endereço gravado — desmarcado ali esconderia dado que
   * existe. Enquanto estiver desmarcado, nenhum campo de endereço é enviado.
   */
  const [showAddress, setShowAddress] = useState(() =>
    hasAddressContent(user?.address),
  );

  const [institutions, setInstitutions] = useState<InputOptions[]>([]);

  // O papel escolhido decide se o campo de instituição aparece, então precisa ser
  // estado — o `getData()` do unform só é lido no submit, tarde demais para o render.
  const [selectedProfile, setSelectedProfile] = useState<string>(
    user?.role ?? '',
  );

  // Gênero e UF pelo mesmo motivo do perfil: são `react-select`, e o `setData` do
  // unform não os alcança (o `InputSelect` registra só `getValue`). Sem estado, os
  // três abriam em branco na edição mesmo com valor gravado.
  const [selectedGender, setSelectedGender] = useState<string>(
    user?.gender ?? '',
  );
  const [selectedAddressUf, setSelectedAddressUf] = useState<string>(
    user?.address?.uf ?? '',
  );

  useCurrentEffect(
    (isCurrent) => {
      if (!isCreating) {
        return;
      }

      findInstitutionsByFilter({
        orderBy: 'institutionName',
        ordenationType: 'ASC',
        // A base do piloto tem poucas instituições (o município e a linha sintética
        // da plataforma); paginar um select seria complexidade sem caso de uso.
        limit: 100,
        page: 1,
      })
        .then(({ list }) => {
          isCurrent() &&
            setInstitutions(
              list.map(({ id, name }) => ({
                value: id ?? '',
                label: name ?? '',
              })),
            );
        })
        .catch(() => {
          isCurrent() &&
            toast.error(
              'Não foi possível carregar as instituições. Recarregue a página.',
            );
        });
    },
    [isCreating],
  );
  useEffect(() => {
    if (user) {
      formRef.current?.setData({
        username: user.draft?.username ?? user.username,
        email: user.draft?.email ?? user.email,
        name: user.name,
        cpf: user.cpf,
        birthday: user.birthday
          ? format(addHours(new Date(user?.birthday), 3), 'yyyy-MM-dd')
          : undefined,
        phone: user.phone,
        gender: user.gender,
      } as PersonalDataProps);

      // `user` chega assíncrono na edição, depois do estado inicial. Só liga, nunca
      // desliga: desmarcar é ação de quem está na tela, e um re-render do `user` não
      // pode desfazê-la.
      if (hasAddressContent(user.address)) {
        setShowAddress(true);
      }
    }
  }, [user]);

  /**
   * O Perfil de permissões precisa de efeito próprio porque depende de **duas**
   * coisas assíncronas: o `user` e as `profiles`, que vêm de `GET /roles`.
   *
   * ⚠️ **Nada de `setData` aqui.** No unform 2.x ele **não é atualização
   * parcial**: percorre todos os campos registrados e escreve `undefined` nos que
   * o objeto não traz — o que apagava CPF e Celular (campos com máscara, que
   * registram `setValue`) e travava o wizard, porque eles são obrigatórios. Quem
   * preenche o select é o estado; para escrever um campo só, use `setFieldValue`.
   */
  useEffect(() => {
    if (!user?.role || !profiles.length) {
      return;
    }

    setSelectedProfile(user.role);
  }, [user, profiles]);

  useEffect(() => {
    if (user?.gender) setSelectedGender(user.gender);
    if (user?.address?.uf) setSelectedAddressUf(user.address.uf);
  }, [user]);

  /**
   * Efeito separado, e não parte do `setData` acima, por causa da ordem: quando o
   * `user` chega depois do primeiro render, ligar o checkbox só **monta** os campos
   * no render seguinte — um `setData` disparado antes disso cairia em campo que o
   * unform ainda não registrou, e o endereço gravado sumiria da tela. Aqui roda
   * depois de `showAddress` virar true, com os campos já montados.
   */
  useEffect(() => {
    if (user && showAddress) {
      // `setFieldValue`, e não `setData`: ver a nota do efeito do perfil acima.
      // Com `setData` este bloco apagava CPF e Celular de todo usuário que tivesse
      // endereço gravado, porque eles não estão neste objeto.
      const endereco: Record<string, string | number | undefined> = {
        street: user.address?.street,
        number: user.address?.number,
        complement: user.address?.complement,
        cep: user.address?.cep,
        city: user.address?.city,
        neighborhood: user.address?.neighborhood,
        addressUf: user.address?.uf,
      };

      Object.entries(endereco).forEach(([campo, valor]) =>
        formRef.current?.setFieldValue(campo, `${valor ?? ''}`),
      );
    }
  }, [user, showAddress]);

  const getValidate = useCallback(async () => {
    const data: PersonalDataProps | undefined = formRef?.current?.getData();

    if (data?.phone && data?.phone.indexOf('_') !== 1) {
      data.phone = data.phone.replace('_', '');
    }

    if (data?.birthday && data?.birthday.indexOf('_') !== 1) {
      data.birthday = data.birthday.replace('_', '');
    }

    if (data?.username) {
      data.username = data?.username?.trim().toLowerCase();
    }

    if (data?.email) {
      data.email = data?.email?.trim().toLowerCase();
    }

    try {
      formRef.current?.setErrors({});

      const schema = Yup.object().shape({
        username: Yup.string()
          .required('O nome de usuário é obrigatório')
          .test(
            'space',
            'Digite um username sem espaço',
            (value) => value.indexOf(' ') < 0,
          ),
        email: Yup.string()
          .required('O e-mail é obrigatório')
          .email('Digite um email válido'),
        name: Yup.string()
          .required('O nome é obrigatório')
          .test(
            'nome',
            'O nome deve ter pelo menos duas palavras',
            (value: string) => {
              const words = value.trim().split(' ');
              return words.length > 1 && words.every((i) => i);
            },
          ),
        cpf: Yup.string()
          .required('O CPF é obrigatório')
          .length(14, 'Digite um CPF válido')
          .test('cpf', 'Digite um CPF válido', (value) => validationCPF(value)),
        birthday: Yup.string().required('A data de nascimento é obrigatória'),
        phone: Yup.string()
          .required('O número de telefone é obrigatório')
          .length(16, 'Digite um número de telefone válido'),
        street: Yup.string(),
        profileType: Yup.string().required(
          'O perfil de permissão do usuário é obrigatório',
        ),
        // Espelha a regra do back (0.27): exigida para papel de equipe e **recusada**
        // para `administrator`, que é o único papel cross-tenant e por isso não tem
        // vínculo com instituição alguma. Sem esta validação o usuário só descobriria
        // no 400 do servidor, depois de preencher o formulário inteiro.
        institutionId: Yup.string().when('profileType', {
          is: (profileType: string) =>
            !user?.id && profileType !== ADMINISTRATOR_ROLE,
          then: Yup.string().required('A instituição é obrigatória'),
          otherwise: Yup.string(),
        }),
        number: Yup.string(),
        complement: Yup.string(),
        cep: Yup.string().test(
          'length',
          'Digite um CEP válido',
          (value) => !!(!value || value.length === 9),
        ),
        city: Yup.string(),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      await validateUserDataService({
        id: user?.id,
        username: data?.username,
        email: data?.email,
        cpf: data?.cpf,
        phone: data?.phone,
      });

      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);
      } else if (err.response) {
        toast.error(err.response.data.message);
      } else {
        toast.error('Entre em contato com o administrador.');
      }

      return false;
    }
  }, [user]);

  const getData = useCallback(() => {
    const data: PersonalDataProps | undefined = formRef.current?.getData();

    return {
      localSensitiveData: {
        email: data?.email?.trim().toLowerCase(),
        username: data?.username?.trim().toLowerCase(),
        profileType: data?.profileType,
      },
    };
  }, []);

  /**
   * Com o checkbox desmarcado devolve `undefined`, e a chave some do corpo — é o que
   * evita a linha de strings vazias em `addresses`. Marcado, mantém o merge com o
   * endereço já existente, para não apagar campo que a tela não mostra (o `id`).
   */
  const buildAddress = useCallback(
    (data?: PersonalDataProps): Address | undefined => {
      if (!showAddress) {
        return undefined;
      }

      const filled = {
        cep: data?.cep,
        city: data?.city,
        complement: data?.complement,
        neighborhood: data?.neighborhood,
        number: data?.number,
        street: data?.street,
        uf: data?.addressUf,
      };

      return user?.address ? { ...user?.address, ...filled } : filled;
    },
    [showAddress, user],
  );

  const handleNext = useCurrentCallback(
    (isCurrent) => async () => {
      if (isCurrent()) {
        const data: PersonalDataProps | undefined = formRef?.current?.getData();

        // O vínculo não cabe em `saveLocal` (recebe `UserBase`, e instituição não é
        // atributo do usuário) e o `POST /users` sai do último passo do wizard, que
        // não conhece esta tela. Fica guardado no hook até lá — sem isto a
        // instituição escolhida aqui se perdia e o back recusava com 400.
        setPendingInstitutionId(
          data?.profileType !== ADMINISTRATOR_ROLE
            ? data?.institutionId
            : undefined,
        );

        saveLocal({
          username: data?.username?.trim().toLowerCase(),
          email: data?.email?.trim().toLowerCase(),
          name: data?.name,
          role: data?.profileType,
          cpf: data?.cpf,
          birthday: data?.birthday,
          phone: data?.phone,
          gender: data?.gender,
          address: buildAddress(data),
        });
        return true;
      }

      return false;
    },
    [saveLocal, setPendingInstitutionId, buildAddress],
  );

  const handleSaveUser = useCurrentCallback(
    (isCurrent) => async () => {
      const data: PersonalDataProps | undefined = formRef?.current?.getData();

      if (data?.username) {
        data.username = data.username.trim();
      }

      try {
        if (isCurrent()) {
          const didSave = await save(
            {
              username: data?.username?.trim().toLowerCase(),
              email: data?.email?.trim().toLowerCase(),
              name: data?.name,
              role: data?.profileType,
              cpf: data?.cpf,
              birthday: data?.birthday,
              phone: data?.phone,
              gender: data?.gender,
              address: buildAddress(data),
            },
            // Fora do objeto de usuário de propósito: é vínculo, não atributo. E só
            // para papel de equipe — mandá-lo com `administrator` é **400** no back.
            data?.profileType !== ADMINISTRATOR_ROLE
              ? data?.institutionId
              : undefined,
          );

          return didSave;
        }

        return false;
      } catch (err) {
        if (err.response) {
          toast.error(err.response.data.message);
        } else {
          toast.error('Entre em contato com o administrador.');
        }
      }

      return false;
    },
    [save, buildAddress],
  );

  const handleSave = useCallback(async () => {
    if (await handleSaveUser()) {
      onSaveSucceed && onSaveSucceed();
      return true;
    }

    return undefined;
  }, [handleSaveUser, onSaveSucceed]);

  const handleCancel = useCallback(async () => {
    cleanLocal();
    return true;
  }, [cleanLocal]);

  const handleCepSearch = useCallback(async () => {
    await getAddressData(formRef.current?.getFieldValue('cep'));
  }, [getAddressData]);

  useEffect(() => {
    if (addressData) {
      formRef.current?.setFieldValue('street', addressData.street);
      formRef.current?.setFieldValue('neighborhood', addressData.neighborhood);
      formRef.current?.setFieldValue('city', addressData.city);
      formRef.current?.setFieldValue('addressUf', addressData.uf);
    }
  }, [addressData]);

  useEffect(() => {
    if (error) {
      formRef.current?.clearField('street');
      formRef.current?.clearField('neighborhood');
      formRef.current?.clearField('city');
      formRef.current?.setFieldValue('addressUf', '');

      toast.error(error);
    }
  }, [error]);

  useImperativeHandle(ref, () => ({
    back: async () => true,
    cancel: handleCancel,
    next: handleNext,
    save: handleSave,
    finish: handleSaveUser,
    getData,
    canSave: getValidate,
  }));

  // Só serve à criação, em que não há `user` a esperar. Na edição os selects são
  // preenchidos pelos efeitos acima, porque o `react-select` não relê o
  // `defaultValue` depois de montado.
  const initialData = {
    gender: user?.gender,
    profileType: user?.role,
    addressUf: user?.address?.uf,
  };

  return (
    <Container>
      <h1>Dados Pessoais</h1>
      <Form ref={formRef} onSubmit={() => {}} initialData={initialData}>
        <GridContent>
          <Field gridArea="lu">
            <label>
              Login do usuário
              <RequiredFieldInformation />
            </label>
            <InputWithoutTitleAndFormattedToLower name="username" />
          </Field>
          <Field gridArea="em">
            <label>
              E-mail
              <RequiredFieldInformation />
            </label>
            <InputWithoutTitleAndFormattedToLower name="email" />
          </Field>
          <Field gridArea="no">
            <label>
              Nome
              <RequiredFieldInformation />
            </label>
            <InputWithoutTitle name="name" />
          </Field>
          <Field gridArea="pu">
            <label>
              Perfil de permissões do usuário
              <RequiredFieldInformation />
            </label>
            <InputSelect
              name="profileType"
              options={profiles}
              placeholder=""
              disabled={!canChangeProfile}
              // Controlado por estado, como o select de instituição do cadastro de
              // paciente. Não basta o `setData`/`defaultValue`: o `react-select` lê
              // `defaultValue` **só na montagem**, e nesta tela as duas pontas chegam
              // depois — o usuário por `GET /users/{id}` e as opções por `GET /roles`.
              // Sem isto o campo abria em branco na edição, sugerindo que salvar
              // apagaria o perfil.
              value={
                profiles.find(({ value }) => value === selectedProfile) ?? null
              }
              // `react-select`, não `<select>` nativo: o handler recebe a opção, não
              // um evento — `event.target.value` viria `undefined` em silêncio.
              onChange={(option) =>
                setSelectedProfile((option as InputOptions)?.value ?? '')
              }
            />
          </Field>
          {/*
            Vínculo com instituição (Fase 0). Só na criação, e só para papel que tem
            tenant: `administrator` é cross-tenant e o back recusa o campo com 400.
          */}
          {isCreating && selectedProfile !== ADMINISTRATOR_ROLE && (
            <Field gridArea="in">
              <label>
                Instituição vinculada
                <RequiredFieldInformation />
              </label>
              <InputSelect
                name="institutionId"
                options={institutions}
                placeholder=""
              />
            </Field>
          )}
          <Field gridArea="cp">
            <label>
              CPF <RequiredFieldInformation />
            </label>
            <InputMaskAlternative
              mask="999.999.999-99"
              title=""
              placeholder="000.000.000-00"
              type="text"
              name="cpf"
            />
          </Field>
          <Field gridArea="dn">
            <label>
              Data de Nascimento <RequiredFieldInformation />
            </label>
            <InputWithoutTitle
              className="datepicker"
              type="date"
              name="birthday"
              max={new Date().toISOString().split('T')[0]}
            />
          </Field>
          <Field gridArea="cl">
            <label>
              Celular (DDD + Número) <RequiredFieldInformation />
            </label>
            <InputMaskAlternative
              type="text"
              name="phone"
              title=""
              mask="(99) 9 9999-9999"
              placeholder="(00) 0 0000-0000"
            />
          </Field>
          <Field gridArea="gn">
            <label>Gênero</label>
            <InputSelect
              name="gender"
              options={genders}
              placeholder=""
              value={
                genders.find(({ value }) => value === selectedGender) ?? null
              }
              onChange={(option) =>
                setSelectedGender((option as InputOptions)?.value ?? '')
              }
            />
          </Field>
        </GridContent>

        <AddressToggle>
          <InputCheckWithoutForm
            id="showAddress"
            checked={showAddress}
            onChange={(event) => setShowAddress(event.target.checked)}
          />
          <label htmlFor="showAddress">Informar endereço</label>
        </AddressToggle>

        {showAddress && (
          <AddressGrid>
            <Field gridArea="ce">
              <label>CEP</label>
              <div className="cep">
                <InputMaskAlternative
                  mask="99999-999"
                  placeholder="00000-000"
                  title=""
                  type="text"
                  name="cep"
                />
                <button type="button" onClick={handleCepSearch}>
                  <IoMdSearch />
                </button>
              </div>
            </Field>
            <Field gridArea="ed">
              <label>Endereço</label>
              <InputWithoutTitle name="street" />
            </Field>
            <Field gridArea="nm">
              <label>Número</label>
              <InputWithoutTitle name="number" type="number" />
            </Field>
            <Field gridArea="cm">
              <label>Complemento</label>
              <InputWithoutTitle name="complement" />
            </Field>
            <Field gridArea="ba">
              <label>Bairro</label>
              <InputWithoutTitle name="neighborhood" />
            </Field>
            <Field gridArea="cd">
              <label>Cidade</label>
              <InputWithoutTitle name="city" />
            </Field>
            <Field gridArea="uf">
              <label>Estado</label>
              <InputSelect
                name="addressUf"
                options={ufProps}
                placeholder=""
                value={
                  ufProps.find(({ value }) => value === selectedAddressUf) ??
                  null
                }
                onChange={(option) =>
                  setSelectedAddressUf((option as InputOptions)?.value ?? '')
                }
              />
            </Field>
          </AddressGrid>
        )}
      </Form>
    </Container>
  );
};

export default forwardRef(PersonalData);
